// /frontend/dashboard/src/router/index.js
// Root router aggregator for dashboard application modules.
// English explanations are followed by Thai explanations for router-design validation and debugging.

import { createRouter, createWebHistory } from 'vue-router';
import { useAuth } from '../composables/useAuth/useAuth.js';
import { DASHBOARD_DEBUG as ROUTER_DEBUG } from '../utils/debugGate.js';

import companyRoutes from './modules/companyRoutes';

import adminRoutes from './modules/adminRoutes';
import alertRoutes from './modules/alertRoutes';
import authRoutes from './modules/authRoutes';
import dashboardRoutes from './modules/dashboardRoutes';
import missionRoutes from './modules/missionRoutes';
import robotRoutes from './modules/robotRoutes';
import setupRoutes from './modules/setupRoutes';

const ROUTER_DEBUG_NAMESPACE = '[router/index]';

function readStoredToken() {
  try {
    return localStorage.getItem('auth_token');
  } catch (_) {
    return null;
  }
}

function parseJwtPayload(token) {
  if (!token || typeof token !== 'string') return null;

  try {
    const parts = token.split('.');
    if (parts.length < 2 || !parts[1]) return null;
    return JSON.parse(atob(parts[1]));
  } catch (_) {
    return null;
  }
}

const routeModules = {
  authRoutes,
  setupRoutes,
  companyRoutes,
  dashboardRoutes,
  robotRoutes,
  missionRoutes,
  alertRoutes,
  adminRoutes,
};

const allRoutes = [
  ...authRoutes,
  ...setupRoutes,
  ...companyRoutes,
  ...dashboardRoutes,
  ...robotRoutes,
  ...missionRoutes,
  ...alertRoutes,
  ...adminRoutes,
];

function collectRouteDiagnostics(routes) {
  const names = routes.map((route) => route.name || `redirect:${route.path}`);
  const paths = routes.map((route) => route.path);
  const duplicates = {
    names: names.filter((name, index) => names.indexOf(name) !== index),
    paths: paths.filter((path, index) => paths.indexOf(path) !== index),
  };

  const sections = routes.reduce((acc, route) => {
    const section = route?.meta?.section || 'unknown';
    acc[section] = (acc[section] || 0) + 1;
    return acc;
  }, {});

  return {
    totalRoutes: routes.length,
    routeNames: names,
    routePaths: paths,
    duplicateNames: [...new Set(duplicates.names)],
    duplicatePaths: [...new Set(duplicates.paths)],
    sectionCounts: sections,
    explanation_en: 'Use this diagnostics snapshot to verify that route modules are aggregated correctly and do not unintentionally conflict.',
    explanation_th: 'ใช้ diagnostics นี้ตรวจว่า route modules ถูกรวมอย่างถูกต้องและไม่มีการชนกันโดยไม่ตั้งใจ',
  };
}

const routerDiagnostics = collectRouteDiagnostics(allRoutes);

const auth = useAuth();

if (ROUTER_DEBUG) {
  // [Debug output disabled — freeze/performance isolation | ปิด debug output ชั่วคราวเพื่อแยกปัญหา freeze/performance]
  // console.log(
  //   `${ROUTER_DEBUG_NAMESPACE} [ROUTER_INIT] English: initializing root router with aggregated route modules | ไทย: กำลังเริ่มต้น root router ด้วย route modules ที่รวมแล้ว`,
  //   {
  //     moduleCounts: {
  //       authRoutes:     authRoutes.length,
  //       setupRoutes:    setupRoutes.length,
  //       companyRoutes:  companyRoutes.length,
  //       dashboardRoutes: dashboardRoutes.length,
  //       robotRoutes:    robotRoutes.length,
  //       missionRoutes:  missionRoutes.length,
  //       alertRoutes:    alertRoutes.length,
  //       adminRoutes:    adminRoutes.length,
  //     },
  //     routerDiagnostics,
  //   },
  // );
}

const router = createRouter({
  history: createWebHistory(),
  routes: allRoutes,
  scrollBehavior(to, from, savedPosition) {
    // English: Build debug payload only when ROUTER_DEBUG is on.
    //          Previously this object was always allocated and then discarded when debug was off.
    // Thai: สร้าง debug payload เฉพาะเมื่อ ROUTER_DEBUG เปิดอยู่
    //       เดิม object นี้ถูกสร้างและทิ้งทุก navigation แม้ debug จะปิดอยู่
    if (ROUTER_DEBUG) {
      // [Debug output disabled — freeze/performance isolation | ปิด debug output ชั่วคราวเพื่อแยกปัญหา freeze/performance]
      // console.log(
      //   `${ROUTER_DEBUG_NAMESPACE} [SCROLL_BEHAVIOR] English: evaluating scroll behavior for route transition | ไทย: กำลังประเมิน scroll behavior สำหรับการเปลี่ยน route`,
      //   {
      //     to:              { name: to?.name || null, path: to?.path || null, fullPath: to?.fullPath || null },
      //     from:            { name: from?.name || null, path: from?.path || null, fullPath: from?.fullPath || null },
      //     hasSavedPosition: Boolean(savedPosition),
      //     explanation_en: 'Scroll behavior is being evaluated for route transition diagnostics.',
      //     explanation_th: 'กำลังประเมิน scroll behavior เพื่อใช้วิเคราะห์การเปลี่ยน route',
      //   },
      // );
    }

    if (savedPosition) {
      return savedPosition;
    }

    return { top: 0, left: 0 };
  },
});

router.beforeEach((to) => {
  // English: Build debug payload only when ROUTER_DEBUG is on.
  //          Previously this object (3 nested objects, ~10 property reads) was always
  //          allocated and discarded on every navigation regardless of the debug flag.
  // Thai: สร้าง debug payload เฉพาะเมื่อ ROUTER_DEBUG เปิดอยู่
  //       เดิม object นี้ (3 nested objects, ~10 property reads) ถูกสร้างและทิ้งทุก
  //       navigation แม้ debug จะปิดอยู่
  if (ROUTER_DEBUG) {
    // [Debug output disabled — freeze/performance isolation | ปิด debug output ชั่วคราวเพื่อแยกปัญหา freeze/performance]
    // console.log(
    //   `${ROUTER_DEBUG_NAMESPACE} [BEFORE_EACH] English: running global beforeEach route validation | ไทย: กำลังรันการตรวจ route แบบ global beforeEach`,
    //   {
    //     to:   { name: to?.name || null, path: to?.path || null, fullPath: to?.fullPath || null, meta: to?.meta || null, params: to?.params || null, query: to?.query || null },
    //     explanation_en: 'Global beforeEach hook helps validate route metadata, permissions, and section grouping before navigation completes.',
    //     explanation_th: 'beforeEach ระดับ global ใช้ตรวจ metadata, permissions และการจัดกลุ่ม section ก่อน navigation เสร็จสมบูรณ์',
    //   },
    // );
  }

  const session = auth.getSessionSnapshot();
  const token = session.token || readStoredToken();
  const requiresAuth = to.meta?.requiresAuth !== false;
  const isGuestOnlyRoute = Boolean(to.meta?.guestOnly);

  // Guest-only guard: authenticated users should not remain on guest-only routes.
  if (isGuestOnlyRoute && session.isHydrated && (session.isAuthenticated || token)) {
    const redirectTarget =
      to.name === 'Login' && typeof to.query?.redirect === 'string' && to.query.redirect
        ? to.query.redirect
        : { name: 'DashboardHome' };

    return redirectTarget;
  }

  // Auth guard: do not force navigation decisions until auth bootstrap has completed.
  // English: If a token exists but hydration has not finished yet, allow App.vue to hold the
  //          loading gate instead of bouncing the user back to /login and causing shell flicker.
  // Thai: ถ้ามี token แต่ hydration ยังไม่เสร็จ ให้ App.vue ถือ loading gate ไว้ก่อน
  //       แทนการเด้งกลับ /login ซึ่งจะทำให้เกิด shell flicker
  if (requiresAuth) {
    if (!session.isHydrated) {
      if (token) {
        return true;
      }

      if (ROUTER_DEBUG) {
        // [Debug output disabled — freeze/performance isolation | ปิด debug output ชั่วคราวเพื่อแยกปัญหา freeze/performance]
        // console.log(
        //   `${ROUTER_DEBUG_NAMESPACE} [AUTH_GUARD_REDIRECT_PRE_HYDRATE] English: no token found before hydration, redirecting to /login | ไทย: ไม่พบ token ก่อน hydration กำลัง redirect ไปยัง /login`,
        //   { to: to.fullPath },
        // );
      }

      return { name: 'Login', query: { redirect: to.fullPath } };
    }

    if (!session.isAuthenticated && !token) {
      if (ROUTER_DEBUG) {
        // [Debug output disabled — freeze/performance isolation | ปิด debug output ชั่วคราวเพื่อแยกปัญหา freeze/performance]
        // console.log(
        //   `${ROUTER_DEBUG_NAMESPACE} [AUTH_GUARD_REDIRECT] English: no authenticated session found, redirecting to /login | ไทย: ไม่พบ authenticated session กำลัง redirect ไปยัง /login`,
        //   { to: to.fullPath },
        // );
      }

      return { name: 'Login', query: { redirect: to.fullPath } };
    }
  }

  // English: If hydration completed and auth says guest while a stale token still exists,
  //          keep protected routes out of the shell by sending the user back to login.
  // Thai: ถ้า hydration เสร็จแล้วแต่ auth เป็น guest ขณะที่ยังมี token ค้างอยู่
  //       ให้กัน route protected ออกจาก shell โดยส่งกลับไปหน้า login
  if (requiresAuth && session.isHydrated && !session.isAuthenticated && token) {
    return { name: 'Login', query: { redirect: to.fullPath } };
  }

  // Super-admin guard: prefer hydrated session role when available; fall back to JWT payload only
  // when auth hydration has not populated user yet.
  if (to.meta?.requiresSuperAdmin) {
    const sessionRole = session.userRole || session.user?.role || null;
    const jwtPayload = parseJwtPayload(token);
    const effectiveRole = sessionRole || jwtPayload?.role || null;

    if (effectiveRole !== 'super_admin') {
      if (ROUTER_DEBUG) {
        // [Debug output disabled — freeze/performance isolation | ปิด debug output ชั่วคราวเพื่อแยกปัญหา freeze/performance]
        // console.log(
        //   `${ROUTER_DEBUG_NAMESPACE} [SUPER_ADMIN_GUARD_REDIRECT] English: role is not super_admin, redirecting to DashboardHome | ไทย: role ไม่ใช่ super_admin กำลัง redirect ไปยัง DashboardHome`,
        //   { to: to.fullPath, role: effectiveRole },
        // );
      }

      return { name: 'DashboardHome' };
    }
  }

  return true;
});

router.afterEach((to, from, failure) => {
  // English: Clear the chunk-reload guard once navigation succeeds so future deploys
  //          can trigger another reload if needed.
  // Thai: ล้าง flag chunk-reload เมื่อ navigation สำเร็จ เพื่อให้ deploy ครั้งหน้า reload ได้อีก
  if (!failure && typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem('_chunk_reload');
  }

  // English: Build debug payload only when ROUTER_DEBUG is on.
  //          Previously this object (2 nested objects, hasFailure, failure, 2 bilingual strings)
  //          was always allocated and discarded on every navigation regardless of the debug flag.
  // Thai: สร้าง debug payload เฉพาะเมื่อ ROUTER_DEBUG เปิดอยู่
  //       เดิม object นี้ (2 nested objects, hasFailure, failure, 2 bilingual strings)
  //       ถูกสร้างและทิ้งทุก navigation แม้ debug จะปิดอยู่
  if (ROUTER_DEBUG) {
    // [Debug output disabled — freeze/performance isolation | ปิด debug output ชั่วคราวเพื่อแยกปัญหา freeze/performance]
    // console.log(
    //   `${ROUTER_DEBUG_NAMESPACE} [AFTER_EACH] English: completed route transition | ไทย: การเปลี่ยน route เสร็จสมบูรณ์แล้ว`,
    //   {
    //     to: {
    //       name: to?.name || null,
    //       path: to?.path || null,
    //       fullPath: to?.fullPath || null,
    //       meta: to?.meta || null,
    //     },
    //     from: {
    //       name: from?.name || null,
    //       path: from?.path || null,
    //       fullPath: from?.fullPath || null,
    //     },
    //     hasFailure: Boolean(failure),
    //     failure: failure || null,
    //     explanation_en: 'Global afterEach hook helps verify final navigation results and whether any navigation failure occurred.',
    //     explanation_th: 'afterEach ระดับ global ใช้ตรวจผลลัพธ์สุดท้ายของ navigation และดูว่ามี navigation failure หรือไม่',
    //   },
    // );
  }
});

router.onError((error) => {
  console.error(
    `${ROUTER_DEBUG_NAMESPACE} [ROUTER_ERROR] English: router runtime error occurred | ไทย: เกิดข้อผิดพลาดระหว่างการทำงานของ router`,
    {
      error: error instanceof Error ? error.message : String(error),
      explanation_en: 'This error hook helps verify lazy import failures, malformed routes, or runtime navigation issues.',
      explanation_th: 'hook นี้ช่วยตรวจ lazy import failures, route ที่ผิดรูปแบบ หรือปัญหา runtime ระหว่าง navigation',
    },
  );
});

export function getRouterDiagnostics() {
  const diagnostics = {
    modules: Object.fromEntries(
      Object.entries(routeModules).map(([key, value]) => [key, value.length]),
    ),
    routerDiagnostics,
    auth: typeof auth.getSessionSnapshot === 'function' ? auth.getSessionSnapshot() : null,
    explanation_en: 'Call this helper to inspect router aggregation, auth session state, and validate route module composition.',
    explanation_th: 'เรียก helper นี้เพื่อดูการรวม router, สถานะ auth session และตรวจ composition ของ route modules',
  };

  if (ROUTER_DEBUG) {
    // [Debug output disabled — freeze/performance isolation | ปิด debug output ชั่วคราวเพื่อแยกปัญหา freeze/performance]
    // console.log(
    //   `${ROUTER_DEBUG_NAMESPACE} [GET_DIAGNOSTICS] English: returning router diagnostics snapshot | ไทย: กำลังส่งคืน snapshot diagnostics ของ router`,
    //   diagnostics,
    // );
  }

  return diagnostics;
}

export default router;
